import { FC, useEffect, useState } from "react";
import { Col, DatePicker, Input, Row, Select } from "antd";
import moment from "moment";
import { useParams } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";

import {
  ICountry,
  ICurrency,
  IJobGrade,
  ILongTermIncentive,
  ILongTermPlan,
  IModal,
} from "@types";

import {
  ErrorServices,
  useCreateLongTermIncentiveMutation,
  useFetchCompanyCountriesQuery,
  useFetchAllCompanyJobGradesQuery,
  useFetchCurrenciesQuery,
  useFetchAllLongTermPlansQuery,
  useUpdateLongTermIncentiveMutation,
} from "@services";

import { useFormValidation, useTypedSelector } from "@hooks";

import {
  showSuccessPopup,
  sortedByName,
} from "@utils";

import CustomModal from "@/components/customModel";

const { Option } = Select;

interface IFormValues {
  grade_id: number | "All Grades" | null;
  country_id: null | number | "ALL";
  currency_id: null | number;
  plan: any;
  is_all_grade: boolean;
  is_global: boolean;
  is_amount: boolean;
  range_type: string;
  year: number | null;
  equity_min: number | null;
  equity_mid: number | null;
  equity_max: number | null;
}

interface IAddLongTermIncentive extends IModal {
  selectedLongTermIncentive: ILongTermIncentive | null;
  setSelectedLongTermIncentive: React.Dispatch<
    React.SetStateAction<ILongTermIncentive | null>
  >;
  isEdit?: boolean;
}

const AddLongTermIncentive: FC<IAddLongTermIncentive> = ({
  isVisible,
  setIsVisible,
  selectedLongTermIncentive,
  setSelectedLongTermIncentive,
  isEdit,
}) => {
  const { plan_id } = useParams<{
    plan_id?: string;
  }>();

  const { company } = useTypedSelector(
    state => state.auth?.user!
  );

  const { id: company_id } = company!;

  const [longTermIncentive, setLongTermIncentive] =
    useState<IFormValues>({
      grade_id: null,
      country_id: null,
      currency_id: null,
      plan: null,
      is_all_grade: false,
      is_global: false,
      is_amount: false,
      range_type: "",
      year: null,
      equity_min: null,
      equity_mid: null,
      equity_max: null,
    });

  const [stock_data, setStockData] =
    useState<any>(null);

  const [planGrades, setPlanGrades] =
    useState<IJobGrade[]>([]);

  const [planCountries, setPlanCountries] =
    useState<ICountry[]>([]);

  const [
    planCountriesForPlan,
    setPlanCountriesForPlan,
  ] = useState<ICountry[]>([]);

  const {
    data: plansData,
    isLoading: isFetchingPlans,
  } = useFetchAllLongTermPlansQuery({
    company_id,
  });

  const { data: plans } = plansData || {};

  const {
    data: jobGradesData,
    isLoading: isFetchingGrades,
  } = useFetchAllCompanyJobGradesQuery({
    company_id,
  });

  const { data: jobGrades } =
    jobGradesData || {};

  const {
    data: countriesData,
    isLoading: isFetchingCountries,
  } = useFetchCompanyCountriesQuery({
    company_id,
  });

  const { data: countries } =
    countriesData || {};

  const {
    data: currenciesData,
    isLoading: isFetchingCurrencies,
  } = useFetchCurrenciesQuery(null);

  const { data: currencies } =
    currenciesData || {};

  const [
    createLongTermIncentive,
    { isLoading: isCreating },
  ] = useCreateLongTermIncentiveMutation();

  const [
    updateLongTermIncentive,
    { isLoading: isUpdating },
  ] = useUpdateLongTermIncentiveMutation();

  const gradeList = planGrades;

  const filteredCountries =
    planCountriesForPlan?.filter(gc =>
      planCountries.some(
        pc => pc.id === gc?.id
      )
    );

  useEffect(() => {
    if (
      plan_id &&
      !selectedLongTermIncentive
    ) {
      const plan = plans?.find(
        (x: any) => x.id === +plan_id
      );

      setLongTermIncentive(prev => ({
        ...prev,
        plan,
        is_all_grade:
          plan?.is_all_grade,
        is_global: plan?.is_global,
      }));

      const normalizedGrades =
        plan?.grade_obj?.length
          ? plan.grade_obj
          : (jobGrades || []).filter(
            (jg: any) =>
              plan?.grades?.includes(
                jg.grade
              )
          );

      setPlanGrades(
        plan?.is_all_grade
          ? jobGrades || []
          : normalizedGrades
      );

      setPlanCountries(
        plan?.is_global
          ? countries
          : plan?.countries
      );
    }
  }, [
    plan_id,
    plans,
    selectedLongTermIncentive,
    countries,
    jobGrades,
  ]);

  useEffect(() => {
    if (
      selectedLongTermIncentive?.id &&
      isEdit
    ) {
      const {
        country,
        plan,
        all_plan_grades,
        all_plan_countries,
        grade_obj,
      } = selectedLongTermIncentive;

      const selectedPlan = plans?.find(
        (x: any) =>
          x.id === +plan?.id
      );

      const planGrades =
        selectedPlan?.grade_obj
          ?.length
          ? selectedPlan.grade_obj
          : (jobGrades || []).filter(
            (jg: any) =>
              selectedPlan?.grades?.includes(
                jg.grade
              )
          );

      const planCountries = (
        jobGrades || []
      )
        .filter((gradeObj: any) =>
          planGrades?.some(
            (pg: any) =>
              pg.id === gradeObj.id
          )
        )
        .flatMap(
          (gradeObj: any) =>
            gradeObj.countries || []
        )
        .filter(
          (c, i, self) =>
            i ===
            self.findIndex(
              sc => sc.id === c.id
            )
        );

      const filteredCountries =
        all_plan_grades
          ? planCountries
          : jobGrades?.find(
            (item: any) =>
              item?.id ===
              grade_obj?.id
          )?.countries || [];

      const longTermIncentive: any =
      {
        ...selectedLongTermIncentive,

        currency_id:
          plan?.currency?.id,

        plan: {
          id: plan?.id,
          name: plan?.name,
        },

        country_id:
          all_plan_countries
            ? "ALL"
            : country?.id ||
            null,

        grade_id: all_plan_grades
          ? "All Grades"
          : grade_obj?.id,

        range_type:
          plan?.is_amount
            ? "Value"
            : "Unit",

        is_global:
          all_plan_countries,

        is_all_grade:
          all_plan_grades,
      };

      delete longTermIncentive?.country;
      delete longTermIncentive?.grade;
      delete longTermIncentive?.grade_obj;

      const filteredCountriesTwo =
        filteredCountries?.filter(
          gc =>
            selectedPlan?.countries?.some(
              pc =>
                pc.id === gc?.id
            )
        );

      setPlanCountries(
        filteredCountriesTwo
      );

      setPlanGrades(planGrades);

      setLongTermIncentive(
        longTermIncentive
      );

      setStockData(
        plan?.stock_data?.[0]
      );

      setPlanCountriesForPlan(
        filteredCountriesTwo
      );
    } else {
      setSelectedLongTermIncentive(
        null
      );

      setLongTermIncentive({
        grade_id: null,
        country_id: null,
        currency_id: null,
        plan: null,
        is_all_grade: false,
        is_global: false,
        is_amount: false,
        range_type: "",
        year: null,
        equity_min: null,
        equity_mid: null,
        equity_max: null,
      });

      setPlanGrades([]);
      setPlanCountries([]);
      setPlanCountriesForPlan([]);
    }
  }, [
    selectedLongTermIncentive,
    isEdit,
    plans,
    jobGrades,
  ]);

  const handleSubmit =
    async () => {
      try {
        const body = {
          ...longTermIncentive,

          plan:
            longTermIncentive?.plan
              ?.id,

          all_plan_countries:
            longTermIncentive?.is_global,

          all_plan_grades:
            longTermIncentive?.is_all_grade,

          grade_id:
            longTermIncentive?.is_all_grade
              ? null
              : longTermIncentive?.grade_id,

          country_id:
            longTermIncentive?.is_global
              ? null
              : longTermIncentive.country_id,
        };

        delete body.is_global;
        delete body.is_all_grade;

        if (
          selectedLongTermIncentive &&
          isEdit
        ) {
          await editLongTermIncentive(
            body
          );
        } else {
          await addLongTermIncentive(
            body
          );
        }

        setIsVisible(false);

        showSuccessPopup({
          title: `Long Term Incentive ${selectedLongTermIncentive
            ? "Updated!"
            : "Created!"
            }`,
          desc: `You have successfully ${selectedLongTermIncentive
            ? "updated the"
            : "created a new"
            } LTI Plan.`,
          role: "client",
        });
      } catch (error) {
        ErrorServices(error);
      }
    };

  const addLongTermIncentive =
    async (body: any) =>
      await createLongTermIncentive({
        company_id,
        body,
      }).unwrap();

  const editLongTermIncentive =
    async (body: any) =>
      await updateLongTermIncentive({
        company_id,
        body,
        id: selectedLongTermIncentive?.id,
      }).unwrap();

  const validation =
    useFormValidation(
      longTermIncentive,
      v => ({
        plan: !v.plan,

        grade_id: !v.grade_id,

        country: !v.country_id,

        currency: !v.currency_id,

        range_type:
          !v.range_type,

        equity_min:
          !v.equity_min,

        equity_mid:
          !v.equity_mid,

        equity_max:
          !v.equity_max,
      })
    );

  return (
    <CustomModal
      width="w-7/12"
      height="600px"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() =>
              setIsVisible(false)
            }
          >
            Cancel
          </button>

          <button
            className={`bg-revampDarkBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!validation.isValid ||
              isCreating ||
              isUpdating
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
            disabled={
              !validation.isValid ||
              isCreating ||
              isUpdating
            }
            onClick={handleSubmit}
          >
            {isCreating ||
              isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${isEdit
        ? "Update"
        : "Create"
        } a Long Term Incentive`}
      isVisible={isVisible}
    >
      <>
        {stock_data && (
          <Row justify="space-between">
            <Col span={24}>
              <h4 className="font-medium text-themeBlack text-lg leading-10">
                Stock Type:{" "}
                <span className="font-semibold">
                  {
                    stock_data?.stock_type
                  }
                </span>
              </h4>

              <h4 className="font-medium text-themeBlack text-lg leading-10">
                Stock Rate:{" "}
                <span className="font-semibold">
                  {stock_data?.stock_value ||
                    100}
                </span>
              </h4>

              <h4 className="font-medium text-themeBlack text-lg leading-10">
                Stock Id:
                <span className="font-semibold">
                  {" "}
                  {
                    stock_data?.tracking_id
                  }
                </span>
              </h4>
            </Col>
          </Row>
        )}

        <Row justify="space-between" className="modal__row">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Type of Plan
            </label>

            <Select
              className="custom-select"
              disabled={
                !!selectedLongTermIncentive ||
                !!plan_id
              }
              loading={isFetchingPlans}
              size="large"
              showSearch={false}
              showArrow
              value={
                longTermIncentive?.plan?.name ||
                undefined
              }
              placeholder="Select plan from here..."
              onChange={(serializedPlan: any) => {
                const parsedPlan = JSON.parse(
                  serializedPlan
                ) as ILongTermPlan;

                const {
                  id,
                  grade_obj,
                  grades,
                  name,
                  countries,
                  is_amount,
                  is_all_grade,
                  stock_data,
                } = parsedPlan;

                setStockData(stock_data?.[0]);

                const normalizedGrades =
                  grade_obj?.length
                    ? grade_obj
                    : (jobGrades || []).filter(
                      (jg: any) =>
                        grades?.includes(
                          jg.grade
                        )
                    );

                setLongTermIncentive(prev => ({
                  ...prev,

                  plan: { id, name },

                  is_global:
                    !countries?.length,

                  is_all_grade,

                  grade_id: null,

                  country_id: null,

                  range_type:
                    is_amount
                      ? "Value"
                      : "Unit",
                }));

                setPlanGrades(
                  normalizedGrades
                );

                setPlanCountries(countries);

                setPlanCountriesForPlan(
                  countries
                );
              }}
            >
              {sortedByName(plans)?.map(
                (plan: ILongTermPlan) => (
                  <Option
                    key={plan?.id}
                    value={JSON.stringify(plan)}
                  >
                    {`${plan?.name} (${plan?.type?.name})`}
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grade
            </label>

            <Select
              className="custom-select"
              disabled={
                !longTermIncentive?.plan
                  ?.name ||
                gradeList?.length === 0
              }
              value={
                longTermIncentive?.grade_id ||
                undefined
              }
              loading={
                !!selectedLongTermIncentive &&
                isFetchingGrades
              }
              showArrow
              size="large"
              showSearch={false}
              placeholder="Select grade from here..."
              optionLabelProp="label"
              onChange={grade_id => {
                if (grade_id === "All Grades") {
                  setLongTermIncentive(
                    prev => ({
                      ...prev,
                      grade_id: "All Grades",
                      is_all_grade: true,
                      country_id: null,
                    })
                  );

                  const planCountries = (
                    jobGrades || []
                  )
                    .filter(
                      (gradeObj: any) =>
                        planGrades.some(
                          (pg: any) =>
                            pg.id ===
                            gradeObj.id
                        )
                    )
                    .flatMap(
                      (gradeObj: any) =>
                        gradeObj.countries ||
                        []
                    )
                    .filter(
                      (c, i, self) =>
                        i ===
                        self.findIndex(
                          sc =>
                            sc.id === c.id
                        )
                    );

                  setPlanCountries(
                    planCountries
                  );

                  return;
                }

                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    grade_id,
                    is_all_grade: false,
                    country_id: null,
                  })
                );

                const findCountries =
                  jobGrades?.find(
                    (item: any) =>
                      item.id === grade_id
                  );

                if (
                  findCountries?.countries
                    ?.length
                ) {
                  setPlanCountries(
                    findCountries.countries
                  );
                }
              }}
            >
              <Option
                key="All Grades"
                value="All Grades"
              >
                All Grades
              </Option>

              {gradeList?.map(
                (grade: IJobGrade) => (
                  <Option
                    key={grade.id}
                    value={grade.id}
                    label={`${grade.grade}`}
                  >
                    {grade?.grade}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Country
            </label>

            <Select
              className="custom-select"
              disabled={
                !longTermIncentive?.grade_id ||
                planCountries?.length === 0
              }
              value={
                longTermIncentive?.country_id ||
                undefined
              }
              showArrow
              loading={
                longTermIncentive?.is_global &&
                isFetchingCountries
              }
              size="large"
              showSearch={false}
              placeholder="Select country from here..."
              onChange={(
                country_id: any
              ) => {
                if (country_id === "ALL") {
                  setLongTermIncentive(
                    (prev: any) => ({
                      ...prev,
                      country_id: "ALL",
                      is_global: true,
                    })
                  );

                  return;
                }

                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    country_id,
                    is_global: false,
                  })
                );
              }}
            >
              {filteredCountries?.length >
                0 && (
                  <Option
                    key="ALL"
                    value="ALL"
                  >
                    All Countries
                  </Option>
                )}

              {sortedByName(
                filteredCountries
              )?.map(
                ({
                  id,
                  name,
                }: ICountry) => (
                  <Option
                    key={id}
                    value={id}
                  >
                    {name}
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Currency
            </label>

            <Select
              className="custom-select"
              showArrow
              showSearch
              loading={
                isFetchingCurrencies
              }
              filterOption={(
                input: any,
                option: any
              ) =>
                option.children
                  .toLowerCase()
                  .indexOf(
                    input.toLowerCase()
                  ) >= 0
              }
              value={
                longTermIncentive?.currency_id ||
                undefined
              }
              onChange={currency_id =>
                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    currency_id,
                  })
                )
              }
              size="large"
              placeholder="Select currency from here..."
            >
              {sortedByName(currencies)?.map(
                ({
                  id,
                  name,
                  code,
                }: ICurrency) => (
                  <Option
                    key={id}
                    value={id}
                  >
                    {`${name} (${code})`}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grant Basis
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              size="large"
              disabled
              value={
                longTermIncentive?.range_type
              }
              placeholder="Enter range type here..."
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Equity Min
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              size="large"
              placeholder="Enter equity min"
              value={
                longTermIncentive?.equity_min ||
                ""
              }
              onChange={e =>
                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    equity_min:
                      +e.target.value,
                  })
                )
              }
            />
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Equity Mid
            </label>

            <Input
              size="large"
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              placeholder="Enter equity mid"
              value={
                longTermIncentive?.equity_mid ||
                ""
              }
              onChange={e =>
                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    equity_mid:
                      +e.target.value,
                  })
                )
              }
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Equity Max
            </label>

            <Input
              size="large"
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              placeholder="Enter equity max"
              value={
                longTermIncentive?.equity_max ||
                ""
              }
              onChange={e =>
                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    equity_max:
                      +e.target.value,
                  })
                )
              }
            />
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={24}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Year
            </label>

            <br />

            <DatePicker
              size="large"
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              value={
                longTermIncentive?.year
                  ? moment(
                    `${longTermIncentive?.year}-1`
                  )
                  : undefined
              }
              onChange={(_, year) =>
                setLongTermIncentive(
                  prev => ({
                    ...prev,
                    year: +year,
                  })
                )
              }
              picker="year"
            />
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddLongTermIncentive;
